import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Clock, MapPin, Phone } from "lucide-react";
import WordFadeIn from "./WordFadeIn";
import DrawIcon from "./DrawIcon";

const hours = [
  { day: "Montag – Samstag", time: "7:00 – 21:00 Uhr" },
  { day: "Sonntag", time: "Geschlossen" },
];

const InfoSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="oeffnungszeiten" ref={ref} className="relative py-20 md:py-32 px-6 md:px-16 bg-secondary/30" aria-label="Öffnungszeiten und Kontakt">
      <div className="max-w-7xl mx-auto">
        {/* Decorative star icon */}
        <DrawIcon
          variant="star"
          size={120}
          delay={0.3}
          className="absolute left-8 md:left-20 bottom-20 z-10 opacity-15 hidden sm:block"
        />

        <div className="mb-12 md:mb-16 text-center md:text-left">
          <p className="font-body text-sm text-primary-bright tracking-[0.3em] uppercase mb-4">
            Besuchen Sie uns
          </p>
          <WordFadeIn
            text="Öffnungszeiten & Kontakt"
            as="h2"
            className="font-display text-3xl sm:text-4xl md:text-6xl font-bold text-foreground leading-tight [hyphens:none]"
            staggerDelay={0.12}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" id="kontakt">
          {/* Hours */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-card p-6 md:p-10 border border-border h-full flex flex-col items-center text-center md:items-start md:text-left"
          >
            <Clock className="w-6 h-6 text-primary-bright mb-5 shrink-0" aria-hidden="true" />
            <h3 className="font-display text-xl font-semibold text-foreground mb-6 [hyphens:none]">
              Öffnungszeiten
            </h3>
            <div className="space-y-3 w-full">
              {hours.map((h) => (
                <div key={h.day} className="flex flex-col sm:flex-row sm:justify-between font-body gap-1">
                  <span className="text-muted-foreground">{h.day}</span>
                  <span className="text-foreground font-medium">{h.time}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Address */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="bg-card p-6 md:p-10 border border-border h-full flex flex-col items-center text-center md:items-start md:text-left"
          >
            <MapPin className="w-6 h-6 text-primary-bright mb-5 shrink-0" aria-hidden="true" />
            <h3 className="font-display text-xl font-semibold text-foreground mb-6 [hyphens:none]">
              Adresse
            </h3>
            <div className="font-body text-muted-foreground space-y-2">
              <p>Rewe Peter Kotlarski OHG</p>
              <p>Bommershöferweg 2–8</p>
              <p>40670 Meerbusch-Osterath</p>
            </div>
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=Bommersh%C3%B6ferweg+2-8%2C+40670+Meerbusch-Osterath"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-6 text-primary-bright font-display text-sm tracking-wide group focus:outline-none focus:ring-2 focus:ring-ring rounded"
            >
              Route planen
              <span className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true">→</span>
            </a>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="bg-card p-6 md:p-10 border border-border h-full flex flex-col items-center text-center md:items-start md:text-left"
          >
            <Phone className="w-6 h-6 text-primary-bright mb-5 shrink-0" aria-hidden="true" />
            <h3 className="font-display text-xl font-semibold text-foreground mb-6 [hyphens:none]">
              Kontakt
            </h3>
            <div className="font-body text-muted-foreground space-y-2">
              <p>
                <a href="tel:+492159912974" className="text-primary-bright underline hover:text-primary-bright/80 transition-colors">
                  +49 2159 912974
                </a>
              </p>
              <p className="mt-4">
                <a href="mailto:info@rewe-kotlarski.de" className="text-primary-bright underline hover:text-primary-bright/80 transition-colors">
                  info@rewe-kotlarski.de
                </a>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default InfoSection;